// src/reportEmail.ts
// Emails each new report to the moderators through Web3Forms
// (https://web3forms.com), a free form-to-email service, so no server or
// billing is needed. The report itself is always saved in Firestore first;
// this is only the heads-up email.
//
// The access key below is meant to be public (it can only send email to
// the address it was created for), so it is fine in the site's code.
const WEB3FORMS_KEY = "de9c8356-1440-4bc9-a870-6be967709f78";

const SITE = "https://2slgbtqiapenpals.org";
const CONSOLE =
  "https://console.firebase.google.com/project/penpalboard/firestore/databases/-default-/data";

export type ReportForEmail = {
  reporter: string;
  reason: string;
  details?: string;
  userId?: string;
  username?: string;
  adId?: string;
  messageId?: string;
  requestId?: string;
  threadId?: string;
  replyId?: string;
  circleId?: string;
  pieceId?: string;
  evidence?: { kind: string; from?: string; subject?: string; body: string };
};

// One line, no control characters, so it is safe in an email subject.
export function oneLine(text: unknown, max = 80) {
  return String(text ?? "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

function indent(text: string) {
  return text
    .slice(0, 10000)
    .split(/\r?\n/)
    .map((l) => `  ${l}`)
    .join("\n");
}

const KIND_LABEL: Record<string, string> = {
  letter: "THE LETTER",
  request: "THE PEN PAL REQUEST",
  post: "THE BOARD POST",
  "room post": "THE COMMUNITY ROOM POST",
  "circle post": "THE PRIVATE CIRCLE POST",
  "circle reply": "THE PRIVATE CIRCLE COMMENT",
  "post piece": "THE PENPAL POST PIECE",
  "post comment": "THE PENPAL POST COMMENT",
  "room reply": "THE COMMUNITY ROOM COMMENT",
};

export function buildReportEmail(
  report: ReportForEmail,
  reportId: string,
  reporterName: string,
  when = new Date()
) {
  const who = report.username ? `@${oneLine(report.username, 40)}` : "";
  const reason = oneLine(report.reason, 120) || "No reason given";
  const subject = `[PenPal Board report] ${reason}${who ? ` about ${who}` : ""}`;

  const lines: string[] = [];
  lines.push("Someone sent a report on PenPal Board. A person needs to review it.", "");
  lines.push(`Reason: ${reason}`);
  lines.push(
    `When: ${when.toLocaleString("en-US", {
      timeZone: "America/New_York",
      dateStyle: "medium",
      timeStyle: "short",
    })} (Eastern)`,
    ""
  );

  lines.push("ABOUT");
  if (report.userId || who) {
    lines.push(`  Person: ${who || "(no username)"}`);
    if (report.userId) {
      lines.push(`  Profile: ${SITE}/profile/${report.userId}`);
      lines.push(`  User ID: ${report.userId}`);
    }
  } else {
    lines.push("  No specific person was picked.");
  }
  if (report.adId) lines.push(`  Board post: ${SITE}/ad/${report.adId}`);
  if (report.messageId) lines.push(`  Letter ID: ${report.messageId}`);
  if (report.requestId) lines.push(`  Pen pal request ID: ${report.requestId}`);
  if (report.pieceId) {
    lines.push(`  PenPal Post piece: ${SITE}/post/${report.pieceId}`);
    if (report.replyId) lines.push(`  Comment ID: ${report.replyId}`);
    if (report.evidence?.from === "anonymous") {
      const key = ["pp", report.pieceId, report.replyId].filter(Boolean).join("_");
      lines.push(`  Written anonymously. The writer's user ID is in Firebase at roomAuthors/${key}`);
    }
  }
  if (report.threadId) {
    if (report.circleId) {
      lines.push(`  Private circle post: ${SITE}/circles/${report.circleId}/post/${report.threadId}`);
      lines.push(`  (In Firebase: circles/${report.circleId}/posts/${report.threadId})`);
    } else {
      lines.push(`  Community post: ${SITE}/community/thread/${report.threadId}`);
    }
    if (report.replyId) lines.push(`  Comment ID: ${report.replyId}`);
    if (report.evidence?.from === "anonymous") {
      const key = [report.circleId, report.threadId, report.replyId].filter(Boolean).join("_");
      lines.push(`  Posted anonymously. The author's user ID is in Firebase at roomAuthors/${key}`);
    }
  }
  lines.push("");

  lines.push("WHAT THEY SAID");
  lines.push(report.details ? indent(report.details) : "  (nothing added)");
  lines.push("");

  const ev = report.evidence;
  if (ev) {
    lines.push(`${KIND_LABEL[ev.kind] || "WHAT WAS REPORTED"} (copied when reported)`);
    if (ev.from) lines.push(`  From: @${oneLine(ev.from, 60)}`);
    if (ev.subject) lines.push(`  Subject: ${oneLine(ev.subject, 300)}`);
    lines.push(indent(ev.body || ""));
    lines.push("");
  }

  lines.push("SENT BY");
  lines.push(`  @${oneLine(reporterName, 40) || "(unknown)"} (user ID ${report.reporter})`);
  lines.push("  The person reported is never told who sent this.", "");

  lines.push(`Open this report in Firebase: ${CONSOLE}/~2Freports~2F${reportId}`);

  return { subject, text: lines.join("\n") };
}

// Something new was dropped in the PenPal Post's mailbox.
export async function emailNewSubmission(p: { id: string; title: string; category: string; username: string }) {
  if (!WEB3FORMS_KEY) return;
  const text = [
    `New writing is waiting in the PenPal Post sorting room.`,
    "",
    `Title: ${oneLine(p.title, 120)}`,
    `Category: ${oneLine(p.category, 40)}`,
    `From: ${p.username === "anonymous" ? "anonymous" : `@${oneLine(p.username, 40)}`}`,
    "",
    `Read it and deliver or return it in the sorting room: ${SITE}/post/sorting`,
  ].join("\n");
  const res = await fetch("https://api.web3forms.com/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      access_key: WEB3FORMS_KEY,
      subject: `[PenPal Board] New PenPal Post submission: ${oneLine(p.title, 60)}`,
      from_name: "The PenPal Post",
      name: "The PenPal Post mailbox",
      "Category": oneLine(p.category, 40),
      message: text,
    }),
  });
  if (!res.ok) throw new Error(`Web3Forms answered ${res.status}`);
}

// A new Private Circle is waiting for approval.
export async function emailCircleRequest(c: {
  id: string;
  name: string;
  purpose: string;
  guidelines: string;
  uid: string;
  username: string;
}) {
  if (!WEB3FORMS_KEY) return;
  const text = [
    `@${oneLine(c.username, 40)} asked to start a Private Circle and be its quartermaster.`,
    "",
    `Name: ${oneLine(c.name, 60)}`,
    "",
    "WHAT IT'S FOR",
    indent(c.purpose),
    "",
    "CIRCLE GUIDELINES",
    indent(c.guidelines || "(none yet)"),
    "",
    `Their profile: ${SITE}/profile/${c.uid}`,
    "",
    "TO APPROVE: open the circle in Firebase and change its `status` field from",
    "`pending` to `approved`. To decline, leave it pending (or delete it).",
    `${CONSOLE}/~2Fcircles~2F${c.id}`,
  ].join("\n");
  const res = await fetch("https://api.web3forms.com/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      access_key: WEB3FORMS_KEY,
      subject: `[PenPal Board] Circle request: ${oneLine(c.name, 60)}`,
      from_name: "PenPal Board",
      name: `@${oneLine(c.username, 40)} (wants to be quartermaster)`,
      "Circle": oneLine(c.name, 60),
      message: text,
    }),
  });
  if (!res.ok) throw new Error(`Web3Forms answered ${res.status}`);
}

export async function emailReport(reportId: string, report: ReportForEmail, reporterName: string) {
  if (!WEB3FORMS_KEY) return; // not set up yet; the report is still saved
  const { subject, text } = buildReportEmail(report, reportId, reporterName);
  const reporter = `@${oneLine(reporterName, 40) || "unknown"}`;
  // Web3Forms shows each field as a row at the top of the email, and shows
  // "Anonymous" when there's no `name`, so the key facts go in fields.
  const res = await fetch("https://api.web3forms.com/submit", {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify({
      access_key: WEB3FORMS_KEY,
      subject,
      from_name: "PenPal Board reports",
      name: `${reporter} (the person who reported)`,
      "Reported by": `${reporter}, user ID ${report.reporter}`,
      "About": report.username
        ? `@${oneLine(report.username, 40)}${report.userId ? `, ${SITE}/profile/${report.userId}` : ""}`
        : "No specific person",
      "Reason": oneLine(report.reason, 120),
      message: text,
    }),
  });
  if (!res.ok) throw new Error(`Web3Forms answered ${res.status}`);
}
